import React from 'react'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import DateCell from 'core/components/listTable/cells/DateCell'
import DataKeys from 'k8s/DataKeys'
import { routes } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import { clusterRoleBindingsSelector } from './selectors'
import { ArrayElement } from 'core/actions/Action'
import { listClusterRoleBindings, deleteClusterRoleBinding } from './new-actions'
import { GridViewColumn } from 'core/elements/grid/Grid'
import useListAction from 'core/hooks/useListAction'
import ListContainer from 'core/containers/ListContainer'
import AddClusterRoleBindingPage from 'k8s/components/rbac/AddClusterRoleBindingPage'
import UpdateClusterRoleBindingPage from 'k8s/components/rbac/UpdateClusterRoleBindingPage'
import InferActionParams from 'core/actions/InferActionParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { useAppSelector } from 'app/store'
import usePluginRouter from 'core/hooks/usePluginRouter'

type ModelDataKey = DataKeys.ClusterRoleBindings
type SelectorModel = ArrayElement<ReturnType<typeof clusterRoleBindingsSelector>>
type ActionParams = InferActionParams<typeof listClusterRoleBindings>

type Params = ActionParams & {
  clusterId?: string
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId']

const defaultParams: Params = {
  clusterId: null,
  healthyClusters: true,
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ClusterRoleBindings',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']

const columns: GridViewColumn<SelectorModel>[] = [
  { key: 'name', label: 'Name' },
  { key: 'clusterName', label: 'Cluster' },
  { key: 'created', label: 'Created', render: (value) => <DateCell value={value} /> },
]

export default function ClusterRoleBindingsListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { currentPluginId } = usePluginRouter()
  const { message, loading, reload } = useListAction(listClusterRoleBindings, {
    params,
    requiredParams,
  })
  const data = useAppSelector(clusterRoleBindingsSelector)

  return (
    <>
      <DocumentMeta title="Cluster Role Bindings" />
      <AddClusterRoleBindingPage />
      <UpdateClusterRoleBindingPage />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ClusterRoleBindings}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        columns={columns}
        data={data}
        getParamsUpdater={getParamsUpdater}
        addUrl={routes.rbac.addClusterRoleBindings.path({ plugin: currentPluginId })}
        addText="Add Cluster Role Binding"
        editUrl={(item, id) =>
          routes.rbac.editClusterRoleBindings.path({
            id,
            clusterId: item.clusterId,
            plugin: currentPluginId,
          })
        }
        deleteAction={deleteClusterRoleBinding}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
